import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import type { LibraryArticleRecord } from '../../types/libraryProvenance';
import { savedFromLabel } from '../../utils/sourcePermissions';
import { SourcePermissionBadges } from './SourcePermissionBadges';
import { resolveSourcePermissions } from '../../utils/sourcePermissions';
import { ExternalLink, Link2, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { LITARCHIVE_PATH } from '../../config/featureNav';

interface LitHubProvenancePanelProps {
  article: LibraryArticleRecord;
  onViewInArchive?: () => void;
}

export const LitHubProvenancePanel: React.FC<LitHubProvenancePanelProps> = ({
  article,
  onViewInArchive,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const permissions = resolveSourcePermissions(article);
  const savedFrom = article.saved_from || 'unknown';

  return (
    <div
      className="mt-3 rounded-lg border border-slate-200 bg-slate-50/80 overflow-hidden"
      data-testid={`lithub-provenance-${article.pmid}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-100/80 transition-colors"
      >
        <span className="text-xs font-medium text-slate-600">
          Saved from {savedFromLabel(savedFrom)}
          {article.folder && article.folder !== 'Ungrouped' && (
            <span className="text-slate-400"> · {article.folder}</span>
          )}
        </span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-200/80 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-slate-400 block">Saved from</span>
              <span className="font-medium text-slate-700">{savedFromLabel(savedFrom)}</span>
            </div>
            {article.clinical_question && (
              <div className="sm:col-span-2">
                <span className="text-slate-400 block">Clinical question</span>
                <span className="text-slate-700">{article.clinical_question}</span>
              </div>
            )}
            {article.search_session_id && (
              <div>
                <span className="text-slate-400 block">Search session</span>
                <span className="font-mono text-slate-600 text-[11px]">{article.search_session_id}</span>
              </div>
            )}
            {article.evidence_packet_id && (
              <div>
                <span className="text-slate-400 block">Evidence packet</span>
                <span className="font-mono text-slate-600 text-[11px] flex items-center gap-1">
                  <Package className="w-3 h-3" /> {article.evidence_packet_id}
                </span>
              </div>
            )}
            {article.ranker_advisory && (
              <div>
                <span className="text-slate-400 block">Ranker advisory</span>
                <Badge variant="outline" className="text-[10px] mt-0.5 capitalize">
                  {article.ranker_advisory.replace(/_/g, ' ')}
                </Badge>
              </div>
            )}
          </div>

          <SourcePermissionBadges badges={permissions.badges} />

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`, '_blank')}
            >
              <ExternalLink className="w-3 h-3 mr-1" /> PubMed
            </Button>
            {onViewInArchive && (
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onViewInArchive}>
                <Link2 className="w-3 h-3 mr-1" /> View in LitArchive
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { LITARCHIVE_PATH };
export default LitHubProvenancePanel;
